import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { aiFetch } from '../utils/aiFetch';
import { EQ_IMAGES } from '../data/images';
import { CATALOG } from '../data/catalog';
import { BRAND_OPTIONS, CUSTOM_OPTION, DUTY_RATED_TYPES, SPEC_FIELDS } from '../data/brands';
import { getEquipmentRowData } from '../export/equipmentData';
import { computeSiteChecks } from '../utils/siteChecks';
import { buildDrawingPdf, prepareSheets, drawingFileName } from '../export/pdfWriter';
import { resolveSettings, TEMPLATES } from '../export/sheetBuilder';
import { PAPER_SIZES } from '../export/paper';
import GatedButton from './GatedButton';

// Equipment drawings are SVG now. When one has to be rasterized (PNG export,
// Blueprint PDF), draw it big so it stays sharp: ~1600px on the long side.
// PNG sources keep their own size (upscaling a bitmap gains nothing).
const rasterSize = (src, img) => {
  const w = img.naturalWidth || 300, h = img.naturalHeight || 300;
  if (!/\.svg(\?|$)/i.test(src)) return { w, h };
  const k = Math.max(1, 1600 / Math.max(w, h));
  return { w: Math.round(w * k), h: Math.round(h * k) };
};

// Convert remote/local image src to a base64 data URL so it can be drawn onto canvas
const toDataUrl = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const c = document.createElement('canvas');
        const { w, h } = rasterSize(src, img);
        c.width = w;
        c.height = h;
        c.getContext('2d').drawImage(img, 0, 0, w, h);
        resolve({ dataUrl: c.toDataURL('image/png'), img });
      } catch {
        resolve({ dataUrl: src, img });
      }
    };
    img.onerror = () => resolve({ dataUrl: src, img: null });
    img.src = src;
  });

// Draw an image into a box preserving aspect ratio (mimics SVG xMidYMid meet)
const drawMeet = (ctx, img, x, y, w, h) => {
  if (!img) return;
  const ia = img.naturalWidth / img.naturalHeight;
  const ba = w / h;
  let dw, dh;
  if (ia > ba) { dw = w; dh = w / ia; }
  else { dh = h; dw = h * ia; }
  ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
};

// Convert a canvas to greyscale in chunks so the UI doesn't freeze
const toGreyscale = (canvas) =>
  new Promise(resolve => {
    const ctx = canvas.getContext('2d');
    const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const d = imgData.data;
    const CHUNK = 40000;
    let i = 0;
    const tick = () => {
      const end = Math.min(i + CHUNK, d.length);
      while (i < end) {
        const g = d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114;
        d[i] = g; d[i + 1] = g; d[i + 2] = g;
        i += 4;
      }
      if (i < d.length) setTimeout(tick, 0);
      else { ctx.putImageData(imgData, 0, 0); resolve(); }
    };
    setTimeout(tick, 0);
  });

// Build the full export canvas — SVG floor plan + info panel below
const buildExportCanvas = async (shop, doors, placed, drains, svgRef, form, greyscale) => {
  const svgEl = svgRef.current;
  if (!svgEl) throw new Error('No SVG found');

  const svgW = parseInt(svgEl.getAttribute('width')) || svgEl.clientWidth || 800;
  const svgH = parseInt(svgEl.getAttribute('height')) || svgEl.clientHeight || 600;
  // These constants (PAD_L/R/T/B, cap 20) must match CadCanvas.jsx's own
  // baseScale/baseOx/baseOy formula exactly. App.js calls resetZoom() right
  // before export, which puts the live SVG at zoom=1/pan=0 — i.e. exactly
  // at baseScale/baseOx/baseOy. If these constants drift from CadCanvas's
  // real ones, the crop below grabs the wrong region (this was the root
  // cause of exports showing the shop cut off / off-center).
  const CAD_PAD_L = 50, CAD_PAD_R = 120, CAD_PAD_T = 80, CAD_PAD_B = 100;
  const availW = svgW - CAD_PAD_L - CAD_PAD_R;
  const availH = svgH - CAD_PAD_T - CAD_PAD_B;
  const shopScale = Math.min(availW / shop.w, availH / shop.h, 20);
  const shopOx = CAD_PAD_L + (availW - shop.w * shopScale) / 2;
  const shopOy = CAD_PAD_T + (availH - shop.h * shopScale) / 2;
  const PAD = 70;
  const cropX = Math.max(0, shopOx - PAD);
  const cropY = Math.max(0, shopOy - PAD);
  const cropW = Math.min(svgW, shop.w * shopScale + PAD * 2 + 80);
  const cropH = Math.min(svgH, shop.h * shopScale + PAD * 2 + 80);
  const SCALE = 2;
  const INFO_H = 240;

  // Pre-cache all equipment images as data URLs to avoid CORS issues during canvas redraw
  const safeImgCache = {};
  for (const [id, src] of Object.entries(EQ_IMAGES)) {
    if (src) safeImgCache[id] = await toDataUrl(src);
  }

  // Clone the SVG so we can strip <image> elements (we'll redraw them ourselves with proper aspect ratio)
  const clone = svgEl.cloneNode(true);
  clone.setAttribute('width', svgW);
  clone.setAttribute('height', svgH);
  clone.setAttribute('viewBox', `0 0 ${svgW} ${svgH}`);
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

  // Collect each <image> element's data + parent transform, then remove it from clone
  const imageData = [];
  clone.querySelectorAll('image').forEach(img => {
    let parent = img.parentElement;
    let transform = '';
    while (parent && parent !== clone) {
      const t = parent.getAttribute('transform');
      if (t) { transform = t; break; }
      parent = parent.parentElement;
    }
    const href = img.getAttribute('href') || img.getAttribute('xlink:href') || '';
    const eqId = Object.keys(EQ_IMAGES).find(k => EQ_IMAGES[k] === href);
    imageData.push({ eqId, x: parseFloat(img.getAttribute('x') || 0), y: parseFloat(img.getAttribute('y') || 0), w: parseFloat(img.getAttribute('width') || 0), h: parseFloat(img.getAttribute('height') || 0), transform });
    img.parentNode.removeChild(img);
  });

  // Inject background rect so SVG renders with our dark theme color
  const bgRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  bgRect.setAttribute('x', '0'); bgRect.setAttribute('y', '0');
  bgRect.setAttribute('width', svgW); bgRect.setAttribute('height', svgH);
  bgRect.setAttribute('fill', '#03080f');
  clone.insertBefore(bgRect, clone.firstChild);

  // Serialize SVG → base64 data URL → load as Image so it can be drawn to canvas
  const svgString = new XMLSerializer().serializeToString(clone);
  const encoded = btoa(unescape(encodeURIComponent(svgString)));
  const svgDataUrl = `data:image/svg+xml;base64,${encoded}`;

  const svgImg = await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('SVG render failed'));
    img.src = svgDataUrl;
  });

  // Build the final canvas at 2x scale for crisp output
  const canvas = document.createElement('canvas');
  canvas.width = cropW * SCALE;
  canvas.height = (cropH + INFO_H) * SCALE;
  const ctx = canvas.getContext('2d');
  ctx.scale(SCALE, SCALE);
  ctx.fillStyle = '#03080f';
  ctx.fillRect(0, 0, cropW, cropH + INFO_H);
  ctx.drawImage(svgImg, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

  // Redraw equipment images with proper aspect ratio + parent group transforms
  for (const imgInfo of imageData) {
    if (!imgInfo.eqId || !safeImgCache[imgInfo.eqId]) continue;
    const { img: eqImg } = safeImgCache[imgInfo.eqId];
    if (!eqImg) continue;
    ctx.save();
    const transMatch = imgInfo.transform.match(/translate\(([^,]+),([^)]+)\)/);
    const rotMatch = imgInfo.transform.match(/rotate\(([^)]+)\)/);
    if (transMatch) ctx.translate(parseFloat(transMatch[1]) - cropX, parseFloat(transMatch[2]) - cropY);
    if (rotMatch) ctx.rotate(parseFloat(rotMatch[1]) * Math.PI / 180);
    drawMeet(ctx, eqImg, imgInfo.x, imgInfo.y, imgInfo.w, imgInfo.h);
    ctx.restore();
  }

  // ===== Info panel below the floor plan =====
  const pY = cropH;
  ctx.fillStyle = '#040b14';
  ctx.fillRect(0, pY, cropW, INFO_H);
  ctx.strokeStyle = '#0066cc'; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(0, pY); ctx.lineTo(cropW, pY); ctx.stroke();

  // Helper to draw text with style
  const txt = (text, x, y, size, color, align = 'left', bold = false) => {
    ctx.fillStyle = color;
    ctx.font = `${bold ? '700' : '400'} ${size}px Arial, sans-serif`;
    ctx.textAlign = align;
    ctx.fillText(String(text), x, y);
  };
  // Same as txt() but truncates with ellipsis if rendered text exceeds maxWidth.
  // Used for column content so long project names / addresses don't bleed into the next column.
  const txtFit = (text, x, y, size, color, maxWidth, bold = false) => {
    ctx.fillStyle = color;
    ctx.font = `${bold ? '700' : '400'} ${size}px Arial, sans-serif`;
    ctx.textAlign = 'left';
    let str = String(text);
    if (ctx.measureText(str).width <= maxWidth) {
      ctx.fillText(str, x, y);
      return;
    }
    // Trim character-by-character until it fits, then append ellipsis
    const ell = '…';
    while (str.length > 0 && ctx.measureText(str + ell).width > maxWidth) {
      str = str.slice(0, -1);
    }
    ctx.fillText(str + ell, x, y);
  };
  // Helper to draw separator lines
  const line = (x1, y1, x2, y2, color = '#162840') => {
    ctx.strokeStyle = color; ctx.lineWidth = 0.5;
    ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
  };

  const pad = 20;
  const col2 = cropW * 0.36;
  const col3 = cropW * 0.66;
  // Max widths for each column's text content (with a small safety gap before next column)
  const COL_GAP = 12;                                  // space between column content and next column
  const w1 = col2 - pad - COL_GAP;                     // Customer column width
  const w2 = col3 - col2 - COL_GAP;                    // Project column width
  const w3 = cropW - col3 - pad - COL_GAP;             // Drawn By column width
  const rooms = placed.filter(p => p.isRoom);
  const equipment = placed.filter(p => !p.isRoom);
  const roomNames = rooms.map(r => r.label || r.name || 'Room').join(', ') || 'None';

  // Header row — logo, app name, date, dimensions
  ctx.fillStyle = '#0055aa';
  ctx.fillRect(pad, pY + 10, 36, 36);
  txt('SB', pad + 18, pY + 33, 14, '#ffffff', 'center', true);
  txt('ShopBuilder Pro', pad + 46, pY + 24, 13, '#e8f4ff', 'left', true);
  txt('CAD Layout Drawing', pad + 46, pY + 38, 9, '#4a7a9b', 'left');
  txt(new Date().toLocaleDateString(), cropW - pad, pY + 22, 9, '#4a7a9b', 'right');
  txt(`${shop.w} x ${shop.h} ${shop.unit}`, cropW - pad, pY + 36, 11, '#ff9900', 'right', true);
  line(pad, pY + 54, cropW - pad, pY + 54);

  // Customer column — txtFit clips long values at column width
  let y1 = pY + 68;
  txt('CUSTOMER', pad, y1, 8, '#3399ff', 'left', true); y1 += 13;
  txtFit(form.customerName || '-', pad, y1, 12, '#e8f4ff', w1, true); y1 += 13;
  txtFit(form.companyName || '-', pad, y1, 10, '#c8dff0', w1); y1 += 12;
  txtFit(form.address || '-', pad, y1, 9, '#6a9fc0', w1); y1 += 11;
  txtFit(form.city || '-', pad, y1, 9, '#6a9fc0', w1); y1 += 11;
  txtFit(form.phone || '-', pad, y1, 9, '#6a9fc0', w1); y1 += 11;
  txtFit(form.email || '-', pad, y1, 9, '#6a9fc0', w1);

  // Project column — this is the column that was overflowing in the bug report
  let y2 = pY + 68;
  txt('PROJECT', col2, y2, 8, '#3399ff', 'left', true); y2 += 13;
  txtFit(form.projectName || '-', col2, y2, 12, '#e8f4ff', w2, true); y2 += 13;
  txtFit(`${shop.w} x ${shop.h} ${shop.unit}`, col2, y2, 10, '#c8dff0', w2); y2 += 12;
  txtFit(`${equipment.length} equipment item${equipment.length !== 1 ? 's' : ''}`, col2, y2, 9, '#6a9fc0', w2); y2 += 11;
  txtFit(`${doors.length} door${doors.length !== 1 ? 's' : ''} (${doors.filter(d => d.type === 'overhead').length} overhead)`, col2, y2, 9, '#6a9fc0', w2); y2 += 11;
  txtFit(`Rooms: ${roomNames}`, col2, y2, 9, '#6a9fc0', w2); y2 += 11;
  if (form.notes) { txtFit(`Notes: ${form.notes}`, col2, y2, 8, '#4a7a9b', w2); }

  // Drawn By column
  let y3 = pY + 68;
  txt('DRAWN BY', col3, y3, 8, '#3399ff', 'left', true); y3 += 13;
  txtFit(form.drawnBy || '-', col3, y3, 12, '#e8f4ff', w3, true); y3 += 34;
  line(col3, y3, cropW - pad, y3);
  y3 += 10; txt('Signature', col3, y3, 8, '#2a4a6a', 'left');
  y3 += 20; txt(new Date().toLocaleDateString(), col3, y3, 9, '#4a7a9b', 'left');
  y3 += 10; txt('Date', col3, y3, 8, '#2a4a6a', 'left');

  // Site conditions row — txtFit on values to prevent overflow into next column
  line(pad, pY + INFO_H - 68, cropW - pad, pY + INFO_H - 68);
  const scY = pY + INFO_H - 54;
  txt('SITE CONDITIONS', pad, scY, 8, '#3399ff', 'left', true);
  const col2b = cropW * 0.33;
  const col3b = cropW * 0.62;
  // Available widths for each value (after the "Label:" text)
  const wA = col2b - (pad + 72) - COL_GAP;
  const wB = col3b - (col2b + 100) - COL_GAP;
  const wC = (cropW - pad) - (col3b + 100);
  txt('Ceiling Height:', pad, scY + 14, 8, '#4a7a9b', 'left');
  txtFit(form.ceilingHeight || '-', pad + 72, scY + 14, 9, '#c8dff0', wA);
  txt('Concrete Thickness:', col2b, scY + 14, 8, '#4a7a9b', 'left');
  txtFit(form.concreteThickness || '-', col2b + 100, scY + 14, 9, '#c8dff0', wB);
  txt('Concrete Condition:', col3b, scY + 14, 8, '#4a7a9b', 'left');
  txtFit(form.concreteCondition || '-', col3b + 100, scY + 14, 9, '#c8dff0', wC);

  // Footer disclaimer
  line(pad, pY + INFO_H - 18, cropW - pad, pY + INFO_H - 18);
  txt('For layout planning purposes only. All dimensions are approximate — verify with manufacturer specs and on-site measurements before installation.',
    cropW / 2, pY + INFO_H - 6, 8, '#2a4a6a', 'center');

  if (greyscale) await toGreyscale(canvas);
  return canvas;
};

// The PDF drawing sheets are built by src/export/ (sheetBuilder + pdfWriter).

export default function ExportPage({ shop, doors, placed, setPlaced, commitHistory, drains, svgRef, form, setForm, onBack }) {
  // Needed for aiFetch() — Clerk tokens are short-lived, so aiFetch calls
  // getToken() fresh at request time rather than us caching it here.
  const { getToken } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [done, setDone] = useState(null);
  const [greyscale, setGreyscale] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  // ─── Drawing sheet settings (persisted in the project via form) ──
  // form.exportSettings holds template/paper/toggles/logo; resolveSettings
  // fills in defaults so old projects without it just work.
  const settings = { ...resolveSettings(form.exportSettings), greyscale };
  const setSetting = (k, v) => setForm(f => ({ ...f, exportSettings: { ...(f.exportSettings || {}), [k]: v } }));
  // Collapsible Equipment List card — starts open so it's not hidden the
  // first time someone lands on the Export page with equipment to label.
  const [equipmentListOpen, setEquipmentListOpen] = useState(true);
  // AI order summary — generated on demand (manual button, not automatic)
  // so we're not firing API calls just because someone opened the Export
  // page. `summaryFlags` holds the deterministic (non-AI) site-check
  // results, kept separate from the free-form AI text.
  const [aiSummary, setAiSummary] = useState('');
  const [summaryFlags, setSummaryFlags] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState('');
  const [includeSummaryPage, setIncludeSummaryPage] = useState(false);

  // ─── Live sheet preview ────────────────────────────────────────
  // Same builder as the PDF (prepareSheets), so the preview IS the output.
  // Rebuilt shortly after any change; `buildId` drops results that arrive
  // after a newer build has started (fast typing in a field).
  const [sheets, setSheets] = useState(null);
  const [sheetIdx, setSheetIdx] = useState(0);
  const [previewError, setPreviewError] = useState('');
  const buildId = useRef(0);
  const summaryForPdf = includeSummaryPage ? { text: aiSummary, flags: summaryFlags } : null;
  const project = { shop, doors, placed, drains, exportForm: form, summary: summaryForPdf };
  const previewKey = JSON.stringify([shop, doors, placed, drains, form, greyscale, summaryForPdf]);
  const projectRef = useRef(project);
  projectRef.current = project;
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  useEffect(() => {
    const id = ++buildId.current;
    const t = setTimeout(() => {
      prepareSheets(projectRef.current, settingsRef.current)
        .then(built => {
          if (id !== buildId.current) return;
          setSheets(built);
          setPreviewError('');
          setSheetIdx(i => Math.min(i, built.pages.length - 1));
        })
        .catch(err => {
          console.error('Sheet preview failed:', err);
          if (id === buildId.current) setPreviewError(err.message || 'Preview failed');
        });
    }, 250);
    return () => clearTimeout(t);
  }, [previewKey]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const equipment = placed.filter(p => !p.isRoom);

  // Update one placed item's label + spec fields when a brand is picked.
  // The dropdown's value arrives as "Make|Model" (a plain string, since HTML
  // <select> options can only carry strings) — we split it back apart and
  // look up the matching brand object to pull its capacity/description too.
  // `brandKey` is stored separately from `label` so the dropdown's selected
  // value doesn't break if someone later hand-edits the label elsewhere
  // (e.g. via the Sidebar) — brandKey is the source of truth for the <select>.
  const handleBrandChange = (uid, brandValue) => {
    setPlaced(prev => prev.map(item => {
      if (item.uid !== uid) return item;
      if (brandValue === CUSTOM_OPTION) {
        return { ...item, label: '', brandKey: '', brandCapacity: '', brandDescription: '', brandLink: '' };
      }
      const [make, model] = brandValue.split('|');
      const brandEntry = (BRAND_OPTIONS[item.id] || []).find(b => b.make === make && b.model === model);
      return {
        ...item,
        label: `${make} ${model}`,
        brandKey: brandValue,
        brandCapacity: brandEntry?.capacity || '',
        brandDescription: brandEntry?.description || '',
        brandLink: brandEntry?.link || '',
      };
    }));
    commitHistory && commitHistory();   // discrete pick — safe to commit right away
    // Picking a real brand means they want the list — switch page 2 on for them
    if (brandValue !== CUSTOM_OPTION) setSetting('showSchedule', true);
  };

  // Update the Heavy Duty / Light Duty toggle for tire equipment (tc/bal/aln).
  // Independent of brand — picking "Hunter TCX" doesn't lock in a duty rating.
  const handleDutyChange = (uid, duty) => {
    setPlaced(prev => prev.map(item => item.uid === uid ? { ...item, dutyRating: duty } : item));
    commitHistory && commitHistory();
  };

  // Update one free-text spec field (HP, tank size, type...) for equipment
  // like air compressors that don't have a real brand catalog yet. Only
  // updates local state on every keystroke — history commit happens on
  // blur instead (see EquipmentRow), so typing "60" doesn't create 2 undo
  // steps for "6" then "60".
  const handleSpecChange = (uid, key, value) => {
    setPlaced(prev => prev.map(item =>
      item.uid === uid ? { ...item, specs: { ...(item.specs || {}), [key]: value } } : item
    ));
  };

  // Generate the AI order summary. Manual trigger only (button click) — no
  // API call fires just from opening the Export page.
  const handleGenerateSummary = async () => {
    setSummaryLoading(true);
    setSummaryError('');
    try {
      // Deterministic checks run FIRST, in plain JS — the AI never decides
      // what's true, it only writes up what we already know for a fact.
      const { flags, notes } = computeSiteChecks(equipment, form, shop);
      setSummaryFlags(flags);

      const equipmentManifest = equipment.map(item => {
        const cat = CATALOG.find(c => c.id === item.id);
        const row = getEquipmentRowData(item, item.id, cat);
        return `- ${row.make} ${row.model} (${cat?.name || item.id})${row.capacity && row.capacity !== '—' ? `, ${row.capacity}` : ''}`;
      }).join('\n');

      const prompt = `You are writing a short, friendly order summary for a customer who just finalized an automotive shop equipment layout. Only use the facts given below — do not invent capacities, requirements, or issues that aren't listed.

EQUIPMENT ORDERED:
${equipmentManifest || 'None placed yet.'}

SITE CONDITIONS:
Ceiling height: ${form.ceilingHeight || 'not specified'} ft
Concrete thickness: ${form.concreteThickness || 'not specified'} in
Concrete condition: ${form.concreteCondition || 'not specified'}
Shop size: ${shop.w} x ${shop.h} ft

ALREADY-VERIFIED ISSUES (state these plainly, don't re-derive or second-guess them):
${flags.length ? flags.join('\n') : 'None found.'}

ADDITIONAL NOTES:
${notes.length ? notes.join('\n') : 'None.'}

Write 2-3 short paragraphs: first summarize what's being ordered in plain language, then cover the notes/issues above clearly but not alarmingly. No markdown, no headers, just plain prose a customer would be comfortable reading.`;

      // Same call shape as your other AI features — aiFetch handles the
      // Clerk token + error mapping, we just give it the Anthropic request
      // body. max_tokens ~800 is plenty for a 2-3 paragraph summary.
      const data = await aiFetch({
        model: 'claude-sonnet-4-5',
        max_tokens: 800,
        messages: [{ role: 'user', content: prompt }],
      }, getToken);

      // Anthropic's Messages API returns `content` as an array of blocks
      // (usually one { type: 'text', text: '...' } block for a plain
      // response) — join any text blocks in case there's more than one.
      const text = (data.content || [])
        .filter(block => block.type === 'text')
        .map(block => block.text)
        .join('\n');
      setAiSummary(text || 'No summary returned.');
    } catch (err) {
      // aiFetch() already throws user-friendly messages (session expired,
      // not authorized, etc.) — just surface them directly.
      console.error('Summary generation failed:', err);
      setSummaryError(err.message || 'Could not generate summary — please try again.');
    } finally {
      setSummaryLoading(false);
    }
  };

  // Generate PNG export and trigger download
  const handleExportPNG = async () => {
    setExporting(true); setShowExportMenu(false);
    try {
      const canvas = await buildExportCanvas(shop, doors, placed, drains, svgRef, form, greyscale);
      const link = document.createElement('a');
      link.download = `ShopBuilder-Layout-${Date.now().toString(36).toUpperCase()}${greyscale ? '-BW' : ''}.png`;
      link.href = canvas.toDataURL('image/png', 1.0);
      link.click();
      setDone('png');
    } catch (err) {
      console.error('PNG export error:', err);
      alert('Export failed: ' + err.message);
    } finally { setExporting(false); }
  };

  // Download the drawing sheets as a vector PDF (selectable text, zoomable)
  const handleExportDrawingPdf = async () => {
    setExporting(true); setShowExportMenu(false);
    try {
      const { pdf, built } = await buildDrawingPdf(project, settings);
      pdf.save(drawingFileName(built.facts));
      setDone('pdf');
    } catch (err) {
      console.error('PDF export error:', err);
      alert('PDF export failed: ' + err.message);
    } finally { setExporting(false); }
  };

  // Company logo for the sheet header — downscaled to a small PNG so it
  // doesn't bloat the project file / autosave (localStorage is ~5 MB).
  const handleLogoFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!/^image\/(png|jpeg)$/.test(file.type)) { alert('Please choose a PNG or JPG image.'); return; }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const s = Math.min(1, 480 / img.width, 160 / img.height);
      const c = document.createElement('canvas');
      c.width = Math.max(1, Math.round(img.width * s));
      c.height = Math.max(1, Math.round(img.height * s));
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      setSetting('logoDataUrl', c.toDataURL('image/png'));
    };
    img.onerror = () => { URL.revokeObjectURL(url); alert('That image could not be read.'); };
    img.src = url;
  };

  return (
    <div style={{ flex: 1, display: 'flex', overflow: 'hidden', background: '#060d18' }}>

      {/* LEFT — form */}
      <div style={{ width: 400, flexShrink: 0, display: 'flex', flexDirection: 'column', background: '#07111e', borderRight: '1px solid #162840' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #162840' }}>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#e8f4ff' }}>Drawing details</div>
          <div style={{ fontSize: 13, color: '#8aa8c5', marginTop: 4, lineHeight: 1.5 }}>
            These print in the title block and header of every sheet.
          </div>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px' }}>
          <Sec title="Sheet">
            {/* Template picker — two cards */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              {[['presentation', 'Soft fills, item names on the plan'], ['blueprint', 'Line work + EQ reference tags']].map(([key, desc]) => {
                const on = settings.template === key;
                return (
                  <button key={key} onClick={() => setSetting('template', key)} aria-pressed={on} style={{
                    flex: 1, textAlign: 'left', padding: '9px 10px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
                    background: on ? 'rgba(44,111,173,.18)' : 'transparent',
                    border: `1px solid ${on ? '#7AB6F1' : '#1e3a54'}`,
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: on ? '#e8f4ff' : '#8aa8c5' }}>{TEMPLATES[key].label}</div>
                    <div style={{ fontSize: 11, color: '#6a8aa8', marginTop: 2, lineHeight: 1.35 }}>{desc}</div>
                  </button>
                );
              })}
            </div>
            <label style={{ display: 'block', marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: '#8aa8c5', marginBottom: 4 }}>Paper size (landscape)</div>
              <select value={settings.paper} onChange={e => setSetting('paper', e.target.value)} style={selectStyle}>
                {Object.entries(PAPER_SIZES).map(([k, p]) => <option key={k} value={k}>{p.label}</option>)}
              </select>
            </label>
            <Row>
              <Fld label="Drawing Number" value={form.drawingNumber || ''} onChange={set('drawingNumber')} placeholder="SB-001" />
              <Fld label="Revision" value={form.revision || ''} onChange={set('revision')} placeholder="A" />
            </Row>
          </Sec>

          <Sec title="Show on Drawing">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 10px' }}>
              {[
                ['showLabels', 'Equipment names'], ['showRefs', 'Reference tags (EQ-01)'],
                ['showDimensions', 'Overall dimensions'], ['showRoomDimensions', 'Room dimensions'],
                ['showGrid', '5 ft drafting grid'], ['showClearance', 'Clearance zones'],
                ['showSchedule', 'Equipment schedule'], ['showNotes', 'General notes'],
                ['showTitleBlock', 'Title block'], ['showSiteConditions', 'Site conditions'],
                ['showSignature', 'Approval signature line'],
              ].map(([k, label]) => (
                <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: '#c8dff0', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!settings[k]} onChange={e => setSetting(k, e.target.checked)} style={{ cursor: 'pointer' }} />
                  {label}
                </label>
              ))}
            </div>
          </Sec>

          <Sec title="Logo (optional)">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {settings.logoDataUrl && (
                <img src={settings.logoDataUrl} alt="Your logo" style={{ height: 36, maxWidth: 120, objectFit: 'contain', background: '#17283C', borderRadius: 4, padding: 3 }} />
              )}
              <label style={{ ...smallBtn, cursor: 'pointer' }}>
                {settings.logoDataUrl ? 'Replace' : 'Upload PNG / JPG'}
                <input type="file" accept="image/png,image/jpeg" onChange={handleLogoFile} style={{ display: 'none' }} />
              </label>
              {settings.logoDataUrl && (
                <button onClick={() => setSetting('logoDataUrl', '')} style={smallBtn}>Remove</button>
              )}
            </div>
            <div style={{ fontSize: 11, color: '#6a8aa8', marginTop: 6 }}>Shows in the sheet header. Sits on navy, so a light or transparent logo reads best.</div>
          </Sec>

          <Sec title="Customer Information">
            <Row>
              <Fld label="Customer Name" value={form.customerName} onChange={set('customerName')} placeholder="Full name" />
              <Fld label="Company / Shop" value={form.companyName} onChange={set('companyName')} placeholder="Business name" />
            </Row>
            <Fld label="Street Address" value={form.address} onChange={set('address')} placeholder="Street and number" />
            <Fld label="City, Province / State" value={form.city} onChange={set('city')} placeholder="City, province / state" />
            <Row>
              <Fld label="Phone" value={form.phone} onChange={set('phone')} placeholder="Phone number" type="tel" />
              <Fld label="Email" value={form.email} onChange={set('email')} placeholder="name@company.com" type="email" />
            </Row>
          </Sec>

          <Sec title="Project Information">
            <Fld label="Project Name" value={form.projectName} onChange={set('projectName')} placeholder="Project name" />
            <Fld label="Custom Notes" value={form.notes} onChange={set('notes')} placeholder="Added to the General Notes — one note per line" textarea />
          </Sec>

          <Sec title="Site Conditions">
            <Row>
              <Fld label="Ceiling Height" value={form.ceilingHeight} onChange={set('ceilingHeight')} placeholder='e.g. 14 ft' />
              <Fld label="Concrete Thickness" value={form.concreteThickness} onChange={set('concreteThickness')} placeholder='e.g. 6"' />
            </Row>
            <Fld label="Concrete Condition" value={form.concreteCondition} onChange={set('concreteCondition')} placeholder="e.g. Good — no cracks, post-tension slab" />
          </Sec>

          <Sec title="Prepared By">
            <Fld label="Your Name" value={form.drawnBy} onChange={set('drawnBy')} placeholder="Your full name" />
          </Sec>
        </div>

        <div style={{ padding: '14px 20px', borderTop: '1px solid #162840', display: 'flex', flexDirection: 'column', gap: 10 }}>

          {/* Export mode — Colour vs Black & White toggle */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#8aa8c5', letterSpacing: '.07em', textTransform: 'uppercase', marginBottom: 6 }}>
              Export Mode
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => setGreyscale(false)} style={{
                flex: 1,
                background: !greyscale ? 'rgba(0,80,180,.15)' : 'transparent',
                border: `1px solid ${!greyscale ? '#3399ff88' : '#1e3a54'}`,
                borderRadius: 6, color: !greyscale ? '#3399ff' : '#4a7a9b',
                fontSize: 12, fontWeight: 600, padding: '8px 0',
                cursor: 'pointer', fontFamily: 'inherit',
              }}>Colour</button>
              <button onClick={() => setGreyscale(true)} style={{
                flex: 1,
                background: greyscale ? 'rgba(180,180,180,.1)' : 'transparent',
                border: `1px solid ${greyscale ? '#aaaaaa66' : '#1e3a54'}`,
                borderRadius: 6, color: greyscale ? '#cccccc' : '#4a7a9b',
                fontSize: 12, fontWeight: 600, padding: '8px 0',
                cursor: 'pointer', fontFamily: 'inherit',
              }}>Black & White</button>
            </div>
          </div>

          {/* Export dropdown (the old Back button here was removed — the
              top-bar stepper handles navigation now) */}
          <div style={{ display: 'flex', gap: 10 }}>

            <div style={{ flex: 1, position: 'relative' }}>
              <button
                onClick={() => setShowExportMenu(m => !m)}
                disabled={exporting}
                style={{
                  width: '100%',
                  background: greyscale
                    ? 'linear-gradient(135deg,#333,#555)'
                    : 'linear-gradient(135deg,#003d80,#0066cc)',
                  border: 'none', borderRadius: 8, color: '#fff',
                  padding: 11, fontSize: 13, fontWeight: 600,
                  cursor: exporting ? 'wait' : 'pointer',
                  fontFamily: 'inherit', opacity: exporting ? 0.7 : 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                {exporting ? 'Generating...' : 'Download drawing'}
                {!exporting && <span style={{ fontSize: 9, opacity: 0.7 }}>▾</span>}
              </button>

              {/* PNG/PDF dropdown menu */}
              {showExportMenu && !exporting && (
                <>
                  <div onClick={() => setShowExportMenu(false)}
                    style={{ position: 'fixed', inset: 0, zIndex: 98 }} />
                  <div style={{
                    position: 'absolute', bottom: '110%', left: 0, right: 0,
                    background: '#0a1628', border: '1px solid #1e3a54',
                    borderRadius: 10, padding: 6, zIndex: 99,
                    boxShadow: '0 -8px 32px rgba(0,0,0,.6)',
                  }}>
                    <button onClick={handleExportPNG} style={exportMenuBtn}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(51,153,255,.08)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      {/* PNG icon — picture frame with download arrow.
                          Wrapped in a fixed 24px slot so the text label below
                          aligns vertically with the PDF row's label. */}
                      <span style={{ width: 24, height: 24, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6a9fc0' }}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <rect x="4" y="4" width="16" height="13" rx="2" stroke="currentColor" strokeWidth="1.8" />
                          <path d="M7 14L10 11L12.5 13.5L14 12L17 15" stroke="currentColor" strokeWidth="1.8" />
                          <circle cx="8.5" cy="8.5" r="1.2" fill="currentColor" />
                          <path d="M12 18V22M12 22L9.5 19.5M12 22L14.5 19.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontWeight: 600 }}>Download PNG</div>
                        <div style={{ fontSize: 11, color: '#8aa8c5' }}>High-resolution image file</div>
                      </div>
                    </button>
                    {/* Drawing PDF — professional sheets from src/export (v4.6) */}
                    <button onClick={handleExportDrawingPdf} style={exportMenuBtn}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,204,255,.08)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <span style={{ width: 24, height: 24, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6a9fc0' }}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path d="M6 3H14L19 8V21H6V3Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                          <path d="M14 3V8H19" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                          <path d="M9 13H15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                          <path d="M9 16H13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                          <path d="M17 15V19M17 19L15.3 17.3M17 19L18.7 17.3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontWeight: 600 }}>Download PDF drawing</div>
                        <div style={{ fontSize: 11, color: '#8aa8c5' }}>
                          {TEMPLATES[settings.template].label} · {PAPER_SIZES[settings.paper].label.split(' (')[0]}{sheets ? ` · ${sheets.pages.length} sheet${sheets.pages.length > 1 ? 's' : ''}` : ''}
                        </div>
                      </div>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Success toast after export */}
          {done && (
            <div style={{ padding: '8px 12px', background: 'rgba(0,140,80,.15)', border: '1px solid #1a5c2a', borderRadius: 6, fontSize: 12, color: '#40c060', fontWeight: 500 }}>
              {done === 'pdf' ? '📄 PDF exported successfully!' : '🖼 Layout exported successfully!'}
            </div>
          )}
        </div>
      </div>

      {/* RIGHT — live preview of the drawing sheets */}
      <div style={{ flex: 1, overflow: 'auto', padding: '24px 32px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          {/* Dims form placeholders (inline styles can't target ::placeholder) */}
          <style>{'.sb-fld::placeholder{color:#4f6d88;opacity:1}'}</style>

          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#e8f4ff', marginBottom: 4 }}>What you'll download</div>
              <div style={{ fontSize: 12, color: '#8aa8c5', lineHeight: 1.6 }}>
                Exact preview of the PDF. The drawing is vector, so it stays sharp when zoomed in.
              </div>
            </div>
            {/* Sheet tabs */}
            {sheets && sheets.pages.length > 1 && (
              <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                {sheets.pages.map((_, i) => (
                  <button key={i} onClick={() => setSheetIdx(i)} aria-pressed={i === sheetIdx} style={{
                    ...smallBtn, padding: '5px 10px',
                    background: i === sheetIdx ? 'rgba(44,111,173,.25)' : 'transparent',
                    borderColor: i === sheetIdx ? '#7AB6F1' : '#1e3a54',
                    color: i === sheetIdx ? '#e8f4ff' : '#8aa8c5',
                  }}>Sheet {i + 1}</button>
                ))}
              </div>
            )}
          </div>

          {/* The sheet itself — the same SVG that goes into the PDF */}
          <div
            role="img"
            aria-label={`Drawing sheet ${sheetIdx + 1} preview`}
            style={{
              background: '#fff', borderRadius: 6, overflow: 'hidden', lineHeight: 0,
              boxShadow: '0 10px 40px rgba(0,0,0,.5)',
              aspectRatio: `${PAPER_SIZES[settings.paper].w} / ${PAPER_SIZES[settings.paper].h}`,
            }}
          >
            {sheets && sheets.pages[sheetIdx] ? (
              <div
                style={{ width: '100%', height: '100%' }}
                // Built by sheetBuilder from escaped project text — not raw user HTML
                dangerouslySetInnerHTML={{ __html: sheets.pages[sheetIdx].svg.replace('<svg ', '<svg style="width:100%;height:100%;display:block" ') }}
              />
            ) : (
              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#66778A', lineHeight: 1.4 }}>
                {previewError ? `Preview failed: ${previewError}` : 'Building preview…'}
              </div>
            )}
          </div>

          {/* ─── Equipment List preview — doubles as the actual editor ───
              This card shows what page 2 of the PDF will look like when the
              checkbox below is on. The brand dropdowns here are live and
              write straight back into placed[] (same as before, just moved
              out of the cramped left sidebar into its own preview card). */}
          <div style={{ marginTop: 28 }}>
            <div
              onClick={() => setEquipmentListOpen(o => !o)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, cursor: 'pointer', userSelect: 'none' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {/* Chevron rotates based on open/closed state — plain CSS
                    transform, no extra icon library needed */}
                <span style={{
                  display: 'inline-block', transition: 'transform .15s',
                  transform: equipmentListOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                  color: '#6a9fc0', fontSize: 12,
                }}>▶</span>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#e8f4ff' }}>Equipment List</div>
              </div>
              <label
                onClick={e => e.stopPropagation()}   // don't collapse the card when clicking the checkbox itself
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#6a9fc0', cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={settings.showSchedule}
                  onChange={e => setSetting('showSchedule', e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                Include schedule in PDF
              </label>
            </div>

            {equipmentListOpen && (
              <>
                <div style={{ fontSize: 11, color: '#4a7a9b', marginBottom: 12, lineHeight: 1.6 }}>
                  Pick a model per unit below — it prints in the Model column of the equipment schedule (with a link to the manufacturer's page).
                </div>

                <div style={{
                  background: '#040b14', border: '1px solid #162840', borderRadius: 10,
                  padding: '16px 18px',
                }}>
                  {equipment.length === 0 ? (
                    <div style={{ fontSize: 11, color: '#4a7a9b' }}>No equipment placed yet.</div>
                  ) : (
                    Object.entries(
                      // Group placed equipment by catalog type so multiples
                      // (e.g. six 2-Post Lifts) sit together under one header.
                      equipment.reduce((groups, item) => {
                        (groups[item.id] = groups[item.id] || []).push(item);
                        return groups;
                      }, {})
                    ).map(([catId, items]) => {
                      const cat = CATALOG.find(c => c.id === catId);
                      const brandList = BRAND_OPTIONS[catId];
                      const isDutyRated = DUTY_RATED_TYPES.includes(catId);
                      const specFields = SPEC_FIELDS[catId];
                      return (
                        <div key={catId} style={{ marginBottom: 16 }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: '#3399ff', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 8 }}>
                            {cat?.name || catId} {items.length > 1 && `(${items.length})`}
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                            {items.map((item, i) => (
                              <EquipmentBrandRow
                                key={item.uid}
                                index={i + 1}
                                item={item}
                                cat={cat}
                                brandList={brandList}
                                isDutyRated={isDutyRated}
                                specFields={specFields}
                                onBrandChange={brandValue => handleBrandChange(item.uid, brandValue)}
                                onDutyChange={duty => handleDutyChange(item.uid, duty)}
                                onSpecChange={(key, value) => handleSpecChange(item.uid, key, value)}
                                onSpecBlur={() => commitHistory && commitHistory()}
                              />
                            ))}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </div>

          {/* ─── AI Order Summary — generated on demand ──────────────────
              Deterministic site checks (computeSiteChecks) run first in
              plain JS; the AI only writes the paragraph, it doesn't decide
              what's true. Gated behind sign-in, same as your other AI
              features (Describe Layout, Import Sketch). */}
          <div style={{ marginTop: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#e8f4ff' }}>Order Summary</div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#6a9fc0', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={includeSummaryPage}
                  onChange={e => setIncludeSummaryPage(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                Add as a sheet in the PDF
              </label>
            </div>
            <div style={{ fontSize: 11, color: '#4a7a9b', marginBottom: 12, lineHeight: 1.6 }}>
              A plain-language summary of what's being ordered, plus anything worth double-checking (ceiling height, bay depth, anchoring) before installation day.
            </div>

            <GatedButton
              onClick={handleGenerateSummary}
              disabled={summaryLoading || equipment.length === 0}
              title="Generate an AI summary of this order"
              style={{
                width: '100%', marginBottom: 12,
                background: 'linear-gradient(135deg,rgba(0,60,140,.3),rgba(0,100,200,.2))',
                border: '1px solid #3399ff44', borderRadius: 8, color: '#3399ff',
                padding: 11, fontSize: 13, fontWeight: 600,
                cursor: summaryLoading ? 'default' : 'pointer', fontFamily: 'inherit',
                opacity: summaryLoading || equipment.length === 0 ? 0.6 : 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              {summaryLoading ? 'Generating…' : 'Generate Summary'}
            </GatedButton>

            {summaryError && (
              <div style={{ fontSize: 11, color: '#ff6666', marginBottom: 12 }}>{summaryError}</div>
            )}

            {/* Flags callout — only the deterministic (non-AI) checks, kept
                visually separate from the AI's free-form text below */}
            {summaryFlags.length > 0 && (
              <div style={{
                background: 'rgba(224,160,48,.1)', border: '1px solid #e0a030',
                borderLeft: '3px solid #e0a030', borderRadius: 6,
                padding: '10px 12px', marginBottom: 12,
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#e0a030', marginBottom: 6 }}>
                  Things to verify before installation
                </div>
                {summaryFlags.map((f, i) => (
                  <div key={i} style={{ fontSize: 11, color: '#d0a860', marginBottom: 3 }}>• {f}</div>
                ))}
              </div>
            )}

            {aiSummary && (
              <textarea
                value={aiSummary}
                onChange={e => setAiSummary(e.target.value)}
                rows={8}
                style={{
                  width: '100%', background: '#040b14', border: '1px solid #162840',
                  borderRadius: 10, color: '#c8dff0', padding: '14px 16px', fontSize: 12,
                  fontFamily: 'inherit', lineHeight: 1.6, outline: 'none', boxSizing: 'border-box',
                  resize: 'vertical',
                }}
              />
            )}
          </div>
        </div>
      </div>

    </div>
  );
}

// Section header wrapper
function Sec({ title, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#66b3ff', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid #162840' }}>
        {title}
      </div>
      {children}
    </div>
  );
}

// Two-column field row
function Row({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>{children}</div>;
}

// Form input field with label (handles both text and textarea)
function Fld({ label, value, onChange, placeholder, type = 'text', textarea }) {
  const s = {
    width: '100%', background: '#040b14', border: '1px solid #162840',
    borderRadius: 6, color: '#e8f4ff', padding: '8px 11px', fontSize: 13,
    fontFamily: 'inherit', outline: 'none', marginBottom: 10,
    display: 'block', resize: 'vertical', boxSizing: 'border-box',
  };
  return (
    <div>
      <label style={{ display: 'block' }}>
        <div style={{ fontSize: 12, fontWeight: 500, color: '#a8c4dc', marginBottom: 4 }}>
          {label}
        </div>
        {/* sb-fld class dims the placeholder (style tag in ExportPage) so hints
            don't look like already-filled-in data */}
        {textarea
          ? <textarea className="sb-fld" value={value} onChange={onChange} placeholder={placeholder} rows={2} style={s} />
          : <input className="sb-fld" type={type} value={value} onChange={onChange} placeholder={placeholder} style={s} />
        }
      </label>
    </div>
  );
}

// One row per placed equipment instance in the Equipment List section.
// Composes up to three independent pieces of UI depending on what this
// catalog type needs (see brands.js for which types get what):
//   - a brand/model dropdown (BRAND_OPTIONS)
//   - a Heavy Duty / Light Duty pill toggle (DUTY_RATED_TYPES)
//   - free-text spec inputs like HP/tank size (SPEC_FIELDS)
// Types with none of the above just show their current name as plain
// text — no empty controls cluttering the list for generic items.
function EquipmentBrandRow({ index, item, cat, brandList, isDutyRated, specFields, onBrandChange, onDutyChange, onSpecChange, onSpecBlur }) {
  const currentLabel = item.label || '';
  const hasAnyControls = brandList || isDutyRated || specFields;

  // "Generic W' x H'" dimension line — shown whenever there's no brand
  // dropdown for this type (benches, compressor, and any other plain
  // catalog item). Computed from the catalog footprint x any resize the
  // customer applied, same math the exported table uses.
  const fw = cat ? (cat.fw * (item.scaleX || 1)) : null;
  const fh = cat ? (cat.fh * (item.scaleY || 1)) : null;
  const genericDims = fw && fh ? `Generic ${fw.toFixed(1)}' × ${fh.toFixed(1)}'` : 'Generic';

  if (!hasAnyControls) {
    return (
      <div style={{ fontSize: 11, color: '#c8dff0', padding: '3px 0', display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ color: '#4a7a9b' }}>#{index}</span>
        <span>{currentLabel || genericDims}</span>
      </div>
    );
  }

  // Dropdown's selected value comes from `brandKey` (e.g. "Rotary|SPO12RA"),
  // not the display label — keeps the <select> in sync even if the label
  // gets hand-edited elsewhere. Falls back to Custom/Other if unset.
  const selectValue = item.brandKey || CUSTOM_OPTION;
  const selectStyle = {
    width: '100%', background: '#040b14', border: '1px solid #162840',
    borderRadius: 6, color: '#e8f4ff', padding: '7px 10px', fontSize: 12,
    fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
  };

  // Same "derive fresh, don't trust stored data" fix as getEquipmentRowData
  // — brandLink didn't exist as a field when older brand picks were made,
  // so this re-looks-it-up from brandList + brandKey instead.
  let derivedLink = '';
  if (brandList && item.brandKey) {
    const [bMake, bModel] = item.brandKey.split('|');
    const entry = brandList.find(b => b.make === bMake && b.model === bModel);
    derivedLink = entry?.link || item.brandLink || '';
  }

  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 11, color: '#8aa8c5', marginBottom: 3 }}>Unit #{index}</div>

      {/* No brand dropdown for this type — show the computed generic
          dimension as a plain line above whatever controls it does have
          (e.g. the free-text notes box for benches/compressor). */}
      {!brandList && (
        <div style={{ fontSize: 11, color: '#8aa5bc', marginBottom: 6 }}>{genericDims}</div>
      )}

      {brandList && (
        <select value={selectValue} onChange={e => onBrandChange(e.target.value)} style={{ ...selectStyle, marginBottom: (isDutyRated || derivedLink) ? 6 : 0 }}>
          {brandList.map(b => (
            <option key={`${b.make}|${b.model}`} value={`${b.make}|${b.model}`}>{b.make} {b.model}</option>
          ))}
          <option value={CUSTOM_OPTION}>{CUSTOM_OPTION}</option>
        </select>
      )}

      {/* Quick link to double-check the picked model before it lands in the
          export — same URL that becomes the clickable hyperlink on page 2. */}
      {brandList && derivedLink && (
        <a
          href={derivedLink}
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: 10, color: '#3399ff', textDecoration: 'underline', display: 'inline-block', marginBottom: isDutyRated ? 6 : 0 }}
        >
          View product ↗
        </a>
      )}

      {/* Heavy/Light pill toggle — independent of brand, click to set,
          click again to clear back to unset */}
      {isDutyRated && (
        <div style={{ display: 'flex', gap: 6, marginBottom: specFields ? 6 : 0 }}>
          {['Heavy Duty', 'Light Duty'].map(duty => {
            const active = item.dutyRating === duty;
            const activeColor = duty === 'Heavy Duty' ? '#ff9900' : '#33cc99';
            return (
              <button
                key={duty}
                type="button"
                onClick={() => onDutyChange(active ? '' : duty)}
                style={{
                  flex: 1, fontSize: 10, padding: '5px 0', borderRadius: 5,
                  border: `1px solid ${active ? activeColor : '#162840'}`,
                  background: active ? `${activeColor}22` : 'transparent',
                  color: active ? activeColor : '#6a9fc0',
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                {duty}
              </button>
            );
          })}
        </div>
      )}

      {/* Free-text spec fields (HP, tank size, type...) — small inputs
          stacked in a row. History commits on blur, not per keystroke. */}
      {specFields && (
        <div style={{ display: 'flex', gap: 6 }}>
          {specFields.map(f => (
            <div key={f.key} style={{ flex: 1, minWidth: 0 }}>
              {/* One-line labels so every input in the row lines up */}
              <div title={f.label} style={{ fontSize: 11, color: '#8aa8c5', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.label}</div>
              <input
                type="text"
                className="sb-fld"
                placeholder={f.placeholder || ''}
                value={item.specs?.[f.key] || ''}
                onChange={e => onSpecChange(f.key, e.target.value)}
                onBlur={onSpecBlur}
                style={{ ...selectStyle, padding: '5px 7px', fontSize: 11, boxSizing: 'border-box' }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Shared style for items inside the export dropdown menu
const selectStyle = {
  width: '100%', background: '#040b14', border: '1px solid #1e3a54', borderRadius: 6,
  color: '#e8f4ff', padding: '8px 10px', fontSize: 12, fontFamily: 'inherit', outline: 'none',
};
const smallBtn = {
  background: 'transparent', border: '1px solid #1e3a54', borderRadius: 6, color: '#8aa8c5',
  padding: '6px 10px', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
};

const exportMenuBtn = {
  display: 'flex', alignItems: 'center', gap: 12,
  width: '100%', background: 'transparent',
  border: 'none', borderRadius: 7, color: '#c8dff0',
  fontSize: 12, fontWeight: 500, padding: '10px 14px',
  cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
  transition: 'background .1s',
};
